/**
 * useAutosave.ts
 *
 * Writes an autosave to IndexedDB after the user has been idle for
 * DEBOUNCE_MS milliseconds. Dirty state is driven externally via the
 * returned `notifyDirty()` callback — callers should invoke it at the
 * same sites they already set `isDirtyRef.current = true` (stroke end,
 * layer duplicate, and the Zustand beforeunload subscriber). This avoids
 * a second independent Zustand subscription that would fire on
 * resetToDefaults() and incorrectly mark a freshly-created project dirty.
 *
 * Also fires immediately when the browser tab becomes hidden (covers the
 * "user closed the tab" case without waiting for the debounce).
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { writeAutosave, clearAutosave as storageClear } from '../utils/autosaveStorage';

const DEBOUNCE_MS = 5_000;

export type AutosaveStatus = 'clean' | 'dirty' | 'saving';

interface UseAutosaveOptions {
  /** Serialises the current project to a JSON string. Provided by Viewport3D. */
  buildJsonRef: React.MutableRefObject<(() => string) | null>;
  /** Called whenever status transitions. */
  onStatusChange?: (status: AutosaveStatus) => void;
}

export function useAutosave({ buildJsonRef, onStatusChange }: UseAutosaveOptions) {
  const timerRef          = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusRef         = useRef<AutosaveStatus>('clean');
  const idleCallbackIdRef = useRef<number | null>(null);
  /** Set to true by cancelPending() so an already-started run() exits early. */
  const abortRef          = useRef(false);

  const setStatus = useCallback((s: AutosaveStatus) => {
    statusRef.current = s;
    onStatusChange?.(s);
  }, [onStatusChange]);

  const doSave = useCallback(() => {
    abortRef.current = false;
    const build = buildJsonRef.current;
    if (!build) return;
    const { currentProjectName } = useAppStore.getState();

    const run = () => {
      idleCallbackIdRef.current = null;
      // If a cancelPending() arrived before we fired, bail out immediately.
      if (abortRef.current) { setStatus('dirty'); return; }
      const json = build();
      // If a cancelPending() arrived while build() was running, skip the write.
      if (abortRef.current) { setStatus('dirty'); return; }
      writeAutosave({ name: currentProjectName, json })
        .then(() => setStatus(abortRef.current ? 'dirty' : 'clean'))
        .catch((err) => {
          console.warn('[autosave] write failed:', err);
          setStatus('dirty');
        });
    };

    if (typeof requestIdleCallback === 'function') {
      idleCallbackIdRef.current = requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  }, [buildJsonRef, setStatus]);

  /**
   * Cancel any pending idle-callback autosave and mark the project dirty so
   * it will retry after the next debounce period.  Call this on pointer-down
   * so an in-flight autosave does not freeze the main thread during a stroke.
   */
  const cancelPending = useCallback(() => {
    if (idleCallbackIdRef.current !== null && typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(idleCallbackIdRef.current);
      idleCallbackIdRef.current = null;
    }
    abortRef.current = true;
    if (statusRef.current === 'saving') {
      setStatus('dirty');
    }
  }, [setStatus]);

  /**
   * Call this wherever the project genuinely becomes dirty (stroke end,
   * layer duplicate, layer/keyframe/fps/totalFrames Zustand changes).
   * It marks status dirty and resets the debounce timer.
   */
  const notifyDirty = useCallback(() => {
    setStatus('dirty');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStatus('saving');
      doSave();
    }, DEBOUNCE_MS);
  }, [doSave, setStatus]);

  // ── Immediate save when tab becomes hidden ────────────────────────────────
  useEffect(() => {
    const onVisChange = () => {
      if (document.visibilityState === 'hidden' && statusRef.current === 'dirty') {
        if (timerRef.current) clearTimeout(timerRef.current);
        setStatus('saving');
        doSave();
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => document.removeEventListener('visibilitychange', onVisChange);
  }, [doSave]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  /** Call after a successful explicit save or new project to reset state. */
  const resetToClean = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    storageClear().catch(() => {});
    setStatus('clean');
  }, [setStatus]);

  return { notifyDirty, resetToClean, cancelPending };
}
